import { type EventForMasterFile, type StoreName, storesWhichSupportDiscountSetters } from '@bluebrick/discounts-service-module'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { now } from '@/common/dateUtils'
import { isDiscountEvent } from '@/common/utils'
import { submitEventsForSetters } from '@/features/discountSetters/discountSettersRemoteRepository'
import { Source, type SourceAccount } from '@/features/discountSetters/types'
import { useEventsStore } from '@/features/events/useEventsStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { type SkuDataWithAccountName } from '@/types'

const useDiscountSettersStoreInner = defineStore('discountSettersInner', () => {
	const submittedEventsToDownload = ref<EventForMasterFile[] | undefined>()
	const sourceAccounts = ref<SourceAccount[]>([])

	return {
		submittedEventsToDownload,
		sourceAccounts
	}
})

const storeToSourceMap = new Map<StoreName, Source>([
	['steam', Source.STEAM_DISCOUNTS],
	['nintendo_other_americas', Source.NINTENDO_DISCOUNTS],
	['nintendo_other_korea', Source.NINTENDO_DISCOUNTS],
	['nintendo_other_taiwan_hong_kong', Source.NINTENDO_DISCOUNTS],
	['nintendo_other_europe_australia', Source.NINTENDO_DISCOUNTS],
	['nintendo_other_japan', Source.NINTENDO_DISCOUNTS],
	['nintendo_other_unknown', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_americas', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_korea', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_taiwan_hong_kong', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_china', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_europe_australia', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_japan', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_unknown', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_southeast_asia', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_2_americas', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_2_europe_australia', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_2_taiwan_hong_kong', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_2_southeast_asia', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_2_korea', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_2_japan', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_2_china', Source.NINTENDO_DISCOUNTS],
	['nintendo_switch_2_unknown', Source.NINTENDO_DISCOUNTS]
])

export const useDiscountSettersStore = defineStore('discountSetters', () => {
	const { storeEvents } = useEventsStore()
	const { trackEvent } = useTelemetry()
	const innerStore = useDiscountSettersStoreInner()

	async function sendEventsToSetters() {
		const skuData: SkuDataWithAccountName[] = []

		storesWhichSupportDiscountSetters.forEach((store) => {
			const accountName = innerStore.sourceAccounts.find((sourceAccount) => sourceAccount.sources.includes(storeToSourceMap.get(store) || ''))
				?.cliParams.user

			storeEvents.get(store)?.skus.forEach(({ events, ...rest }) => {
				const relevantEvents = events.filter((event) => event.start_date > now() && isDiscountEvent(event))

				skuData.push({ ...rest, events: relevantEvents, account_name: accountName })
			})
		})

		const submittedEvents = await submitEventsForSetters(skuData)

		if (submittedEvents) {
			innerStore.submittedEventsToDownload = submittedEvents
		}
	}

	const shouldShowDiscountSettersModal = ref(false)

	function showDiscountSettersModal() {
		trackEvent('user', 'Opened discount setter modal.')
		shouldShowDiscountSettersModal.value = true
	}

	function hideDiscountSettersModal() {
		trackEvent('user', 'Closed discount setter modal.')
		shouldShowDiscountSettersModal.value = false
	}

	const submittedEventsToDownload = computed(() => innerStore.submittedEventsToDownload)

	function updateSourceAccounts(payload: SourceAccount[]) {
		innerStore.sourceAccounts = payload
	}

	return {
		sendEventsToSetters,
		shouldShowDiscountSettersModal,
		showDiscountSettersModal,
		hideDiscountSettersModal,
		submittedEventsToDownload,
		updateSourceAccounts
	}
})
